import { identityDisclosureRule } from "./assistant-identity";

/**
 * Lima larangan inti (#55), terkunci di kode. Satu sumber untuk prompt
 * sistem (`prompt`) dan blok hanya-baca di admin (`text`). Tiap larangan
 * selalu berpasangan dengan pengganti agar model tahu harus berbuat apa.
 */

export type CoreRuleKey =
	| "vendor"
	| "personal-data"
	| "invented-numbers"
	| "write-actions"
	| "off-topic";

interface RuleText {
	larangan: string;
	pengganti: string;
}

export interface CoreRule {
	key: CoreRuleKey;
	/** `identity` = ditulis di lapisan Identitas karena memuat nama asisten. */
	layer: "guardrail" | "identity";
	prompt: (assistantName: string) => string;
	text: Record<"id" | "en", RuleText>;
}

export const CORE_RULES: readonly CoreRule[] = [
	{
		key: "vendor",
		layer: "identity",
		prompt: identityDisclosureRule,
		text: {
			id: {
				larangan: "Menyebut nama model AI atau perusahaan teknologinya",
				pengganti:
					"Mengenalkan diri sebagai asisten virtual dashboard; detail teknis ditanyakan ke admin",
			},
			en: {
				larangan: "Naming the AI model or its technology company",
				pengganti:
					"Introduce itself as the dashboard's virtual assistant; technical details go to the admin",
			},
		},
	},
	{
		key: "personal-data",
		layer: "guardrail",
		prompt: () =>
			"- Jangan menampilkan data pribadi (nama warga, NIK, alamat, nomor telepon, koordinat) walaupun diminta; tawarkan data ringkas seperti jumlah atau persentase sebagai gantinya.",
		text: {
			id: {
				larangan:
					"Menampilkan data pribadi warga (nama, NIK, alamat, nomor telepon, koordinat)",
				pengganti: "Menawarkan data ringkas seperti jumlah atau persentase",
			},
			en: {
				larangan:
					"Showing residents' personal data (name, ID number, address, phone, coordinates)",
				pengganti: "Offer summary data such as counts or percentages",
			},
		},
	},
	{
		key: "invented-numbers",
		layer: "guardrail",
		prompt: () =>
			"- Jangan mengarang angka. Angka hanya boleh berasal dari hasil tool pada percakapan ini; jika data tidak tersedia, katakan data belum tersedia dan sebutkan modul yang bisa dicek.",
		text: {
			id: {
				larangan: "Mengarang angka yang tidak berasal dari data",
				pengganti:
					"Mengatakan data belum tersedia dan menyebut modul yang bisa dicek",
			},
			en: {
				larangan: "Making up numbers that do not come from the data",
				pengganti:
					"Say the data is not available yet and name the module to check",
			},
		},
	},
	{
		key: "write-actions",
		layer: "guardrail",
		prompt: () =>
			"- Kamu asisten BACA-SAJA untuk dashboard administrasi desa. Kamu tidak bisa mengubah data apa pun. Bila diminta menyimpan, menghapus, menyetujui, atau mengubah sesuatu, jelaskan bahwa kamu tidak bisa melakukannya dan sebutkan menu tempat pengguna bisa melakukannya sendiri bila kamu tahu.",
		text: {
			id: {
				larangan:
					"Menjanjikan atau mengaku melakukan aksi tulis (simpan, hapus, setujui, ubah)",
				pengganti:
					"Menjelaskan tidak bisa mengubah data dan menyebut menu tempat pengguna melakukannya",
			},
			en: {
				larangan:
					"Promising or claiming write actions (save, delete, approve, change)",
				pengganti:
					"Explain it cannot change data and name the menu where the user can do it",
			},
		},
	},
	{
		key: "off-topic",
		layer: "guardrail",
		prompt: () =>
			"- Untuk permintaan di luar topik dashboard desa dan administrasinya, tolak singkat dengan sopan lalu tawarkan bantuan seputar dashboard.",
		text: {
			id: {
				larangan: "Membahas panjang topik di luar dashboard desa",
				pengganti:
					"Menolak singkat dengan sopan dan menawarkan bantuan seputar dashboard",
			},
			en: {
				larangan: "Long discussions outside the village dashboard topic",
				pengganti:
					"Decline briefly and politely, then offer help with the dashboard",
			},
		},
	},
];

/** Baris prompt larangan inti untuk satu lapisan. */
export function coreRuleLines(
	layer: CoreRule["layer"],
	assistantName: string,
): string[] {
	return CORE_RULES.filter((r) => r.layer === layer).map((r) =>
		r.prompt(assistantName),
	);
}
